import React from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { request } from '../utils/request';
import CustomTextField from './CustomTextField';

interface ContactInfoProps {
  icon: string;
  text: string;
}

const ContactInfo: React.FC<ContactInfoProps> = ({ icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Icon
      icon={icon}
      width='24'
      height='24'
      style={{ color: 'primary.main', marginRight: '16px' }}
    />
    <Typography variant='body1' sx={{ opacity: 0.8 }}>
      {text}
    </Typography>
  </Box>
);

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone number is required'),
  subject: yup.string().required('Subject is required'),
  message: yup.string().required('Message is required'),
});

const ContactSection: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const contactInfo = [{ icon: 'mdi:phone-outline', text: '+962-79-701-6535' }];

  const formFields = [
    { name: 'name', label: 'Your Name', type: 'text' },
    { name: 'email', label: 'Your Email', type: 'email' },
    { name: 'phone', label: 'Phone Number', type: 'text' },
    { name: 'subject', label: 'Subject', type: 'text' },
    {
      name: 'message',
      label: 'Your Message',
      type: 'text',
      multiline: true,
      rows: 5,
    },
  ];

  const onSubmit = async (data: FormData) => {
    try {
      await request('contact/submit', {
        method: 'POST',
        body: data,
      });

      setSnackbar({
        open: true,
        message: 'Message sent successfully!',
        severity: 'success',
      });

      reset();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : 'Failed to send message',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      id='contactus'
      sx={{
        background: 'background.default',
        py: { xs: 6, md: 10 },
        color: '#fff',
      }}
    >
      <Container maxWidth='lg'>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={6}
          alignItems='center'
        >
          <Box sx={{ width: { xs: '100%', md: '41.66%' } }}>
            <Typography
              variant='h4'
              component='h2'
              sx={{ fontWeight: 700, mb: 3 }}
            >
              Get In Touch with Our{' '}
              <Box component='span' sx={{ color: 'primary.main' }}>
                Team.
              </Box>
            </Typography>

            <Typography variant='body1' sx={{ mb: 4, opacity: 0.8 }}>
              Have a project in mind or just want to say hello? We'd love to
              hear from you. Fill out the form or use the contact details below.
            </Typography>

            {contactInfo.map((info, index) => (
              <ContactInfo key={index} {...info} />
            ))}

            {/* <Box
              sx={{
                width: '100%',
                height: { xs: '200px', md: '250px' },
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                mt: 3,
              }}
            >
              <Icon
                icon='mdi:map-legend'
                width='80'
                height='80'
                style={{ color: 'primary.main', opacity: 0.3 }}
              />
            </Box> */}
          </Box>

          <Box sx={{ width: { xs: '100%', md: '58.33%' } }}>
            <Box
              component='form'
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: { xs: 3, md: 5 },
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              autoComplete='off'
            >
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  {formFields.slice(0, 2).map((field) => (
                    <CustomTextField
                      key={field.name}
                      {...register(field.name as keyof FormData)}
                      name={field.name}
                      label={field.label}
                      type={field.type}
                      error={!!errors[field.name as keyof FormData]}
                    />
                  ))}
                </Stack>
                {formFields.slice(2).map((field) => (
                  <CustomTextField
                    key={field.name}
                    {...register(field.name as keyof FormData)}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    multiline={field.multiline}
                    rows={field.rows}
                    error={!!errors[field.name as keyof FormData]}
                  />
                ))}
                <Button
                  type='submit'
                  variant='contained'
                  size='large'
                  fullWidth
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: '#6a00d6' },
                    borderRadius: '8px',
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Send Message
                </Button>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbar-root': {
            width: '400px',
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            fontSize: '1rem',
            padding: '12px 20px',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactSection;
